import { useEffect } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Sparkles, Zap, BookOpen, Cable, ArrowRight } from 'lucide-react';
import { useSession } from '@/lib/session';
import { startLogin } from '@/lib/oauth';

export const Route = createFileRoute('/')({
  component: WelcomePage,
});

/**
 * Detect `?auto_login=1` query param + kick off OAuth flow kalau belum auth.
 *
 * Dipasang di link sidebar app.balesotomatis.id → user klik → tab baru ke
 * playground.balesotomatis.id/?auto_login=1 → playground deteksi param →
 * langsung redirect ke auth server → karena user udah login di app, auth
 * server silent-approve → balik ke /auth/callback → exchange → logged in.
 *
 * Total ~2 redirect, ~1 detik. User klik 1×, sampe playground sudah auth.
 *
 * Trade-off: parameter di-strip dari URL via history.replaceState() setelah
 * processed supaya refresh page gak re-trigger login redirect loop.
 */
function useAutoLogin() {
  const session = useSession();
  useEffect(() => {
    // Tunggu session resolve (loading=false) — jangan auto-redirect kalau
    // ternyata user udah ada cookie yang masih valid.
    if (session.loading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('auto_login') !== '1') return;

    // Strip param dari URL apa pun status auth-nya, supaya refresh /
    // navigate-back gak loop.
    params.delete('auto_login');
    const newSearch = params.toString();
    const cleanUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);

    // Kalau udah authenticated (shared cookie .balesotomatis.id), no-op.
    if (session.authenticated) return;

    // Kick off OAuth. returnTo: '/' supaya balik ke landing setelah callback.
    void startLogin({ returnTo: '/' });
  }, [session.loading, session.authenticated]);
}

/**
 * Landing page — neo-brutalism hero + colored feature cards.
 *
 * Three primary actions ("Browse / Try-it / Ask AI") get one card each with
 * a circular colored icon, thick black outline, and offset shadow. Each card
 * lifts on hover (translate + bigger shadow). MCP teaser di bawah dengan
 * lilac accent + bigger CTA card.
 */
function WelcomePage() {
  useAutoLogin();
  return (
    <div className="max-w-5xl mx-auto py-12 md:py-20 px-4 md:px-8">
      {/* Hero */}
      <div className="space-y-6 mb-16 max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 h-7 rounded-pill border-2 border-border-strong bg-method-put text-2xs font-mono font-bold uppercase tracking-wider shadow-brutal-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-fg" />
          v1 — 313+ endpoints
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-5xl md:text-6xl leading-[1.05]">
          The CRM API,
          <br />
          <span className="inline-block px-3 -mx-1 bg-accent border-[2.5px] border-border-strong rounded-brutal-sm shadow-brutal mt-2">
            tinggal coba.
          </span>
        </h1>
        <p className="text-lg md:text-xl text-fg-secondary max-w-2xl">
          Browse REST endpoints, fire real requests dengan session lo,
          dan minta AI bikin code snippet — semua dalam satu tempat.
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/endpoints/$tag/$operation"
            params={{ tag: 'activity-log', operation: 'post_activity_log' }}
            className="inline-flex items-center gap-2 px-5 h-12 rounded-pill border-[2.5px] border-border-strong bg-accent shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-sm transition-all font-display font-bold text-sm"
          >
            Start exploring
            <ArrowRight size={16} />
          </Link>
          <a
            href="https://app.balesotomatis.co.id"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 h-12 rounded-pill border-[2.5px] border-border-strong bg-elevated shadow-brutal-sm hover:shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-brutal-none transition-all font-display font-bold text-sm"
          >
            Get your secret_key
          </a>
        </div>
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        <FeatureCard
          icon={<BookOpen size={20} />}
          iconBg="bg-method-post"
          title="Browse API"
          description="Eksplor 313+ endpoint ke-organize per tag. Cari pake ⌘K."
        />
        <FeatureCard
          icon={<Zap size={20} />}
          iconBg="bg-method-put"
          title="Try-it-out"
          description="Form auto-generated dari schema. Session lo ke-attach, response live, snippet siap copy."
        />
        <FeatureCard
          icon={<Sparkles size={20} />}
          iconBg="bg-method-delete"
          title="Ask AI"
          description="Asisten AI streaming yang ngerti seluruh API. Tekan ⌘J kapan aja."
        />
      </div>

      {/* MCP CTA */}
      <Link to="/mcp" className="mt-12 block group">
        <div className="p-6 md:p-8 rounded-brutal border-[2.5px] border-border-strong bg-method-patch shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 transition-all">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-pill border-2 border-border-strong bg-elevated shadow-brutal-sm shrink-0">
              <Cable size={22} />
            </div>
            <div className="flex-1">
              <div className="text-2xs font-mono font-bold uppercase tracking-wider mb-1">
                NEW · MCP server
              </div>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-1">
                Hubungkan AI lo ke API ini.
              </h2>
              <p className="text-fg-secondary">
                Connect Claude Desktop / Cursor / Cline. AI bisa call API atas nama lo,
                pake session yang sama, zero copy-paste.
              </p>
            </div>
            <ArrowRight
              size={28}
              className="hidden md:block shrink-0 group-hover:translate-x-1 transition-transform"
            />
          </div>
        </div>
      </Link>
    </div>
  );
}

function FeatureCard({
  icon,
  iconBg,
  title,
  description,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 rounded-brutal border-[2.5px] border-border-strong bg-elevated shadow-brutal hover:shadow-brutal-lg hover:-translate-y-1 transition-all">
      <div
        className={`inline-flex items-center justify-center w-12 h-12 rounded-pill border-2 border-border-strong shadow-brutal-sm mb-5 ${iconBg}`}
      >
        {icon}
      </div>
      <h3 className="font-display font-extrabold text-xl mb-2">{title}</h3>
      <p className="text-sm text-fg-secondary leading-relaxed">{description}</p>
    </div>
  );
}
